import { useEffect, useRef, type ReactNode } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import guineaStudy from "../data/guinea-study.json";
import { STUDY_LANDSCAPES, STUDY_TERRITORY_TREE, type LandscapeId } from "../data/landscapes";

type GuineaLandscapeMapProps = {
  readonly selectedId: LandscapeId | null;
  readonly onSelect: (id: LandscapeId) => void;
  readonly interactive?: boolean;
  readonly highlightAll?: boolean;
  /** Hide the OpenStreetMap / contours figcaption (hub compact views). */
  readonly showCaption?: boolean;
  /** Optional floating control rendered over the map frame (bottom layer). */
  readonly overlay?: ReactNode;
};

type AdminProperties = {
  readonly id: string;
  readonly name: string;
  readonly level: "country" | "region" | "prefecture";
};

type MarkerEntry = {
  readonly id: LandscapeId;
  readonly marker: L.CircleMarker;
};

const GUINEA_BOUNDS = L.latLngBounds([7.15, -15.4], [12.7, -7.6]);
const STUDY_CENTER: L.LatLngExpression = [9.7, -12.7];

function prefectureStyle(prefId: string, emphasized: boolean): L.PathOptions {
  const forecariah = prefId === "forecariah";
  return {
    color: forecariah ? "#b86a1c" : "#2a6f9e",
    weight: emphasized ? 2.6 : 1.3,
    fillColor: forecariah ? "#c47b2c" : "#3d87b8",
    fillOpacity: emphasized ? 0.32 : 0.1,
    opacity: emphasized ? 1 : 0.55,
  };
}

function countryStyle(): L.PathOptions {
  return {
    color: "#2f4f3d",
    weight: 1.8,
    fillColor: "#cfe0c6",
    fillOpacity: 0.28,
  };
}

function regionStyle(): L.PathOptions {
  return {
    color: "#1f6f4a",
    weight: 2.6,
    dashArray: "7 5",
    fillColor: "#1f6f4a",
    fillOpacity: 0.1,
  };
}

export function GuineaLandscapeMap({
  selectedId,
  onSelect,
  interactive = true,
  highlightAll = false,
  showCaption = true,
  overlay,
}: GuineaLandscapeMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<MarkerEntry[]>([]);
  const layersRef = useRef<L.GeoJSON | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const selectedPrefectureId =
    selectedId == null
      ? null
      : (STUDY_LANDSCAPES.find((p) => p.id === selectedId)?.prefectureId ?? null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const map = L.map(el, {
      center: STUDY_CENTER,
      zoom: 7,
      minZoom: 6,
      maxZoom: 11,
      scrollWheelZoom: false,
      attributionControl: true,
      zoomControl: true,
    });

    map.setMaxBounds(GUINEA_BOUNDS.pad(0.15));

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · frontières <a href="https://www.geoboundaries.org/">geoBoundaries</a>',
    }).addTo(map);

    const collection = guineaStudy as GeoJSON.FeatureCollection<GeoJSON.Geometry, AdminProperties>;

    const geoLayer = L.geoJSON(collection, {
      style: (feature) => {
        const level = feature?.properties?.level ?? "country";
        const prefId = feature?.properties?.id ?? "";
        if (level === "country") return countryStyle();
        if (level === "region") return regionStyle();
        return prefectureStyle(prefId, true);
      },
      onEachFeature: (feature, layer) => {
        const name = feature.properties?.name;
        if (name) layer.bindTooltip(name, { sticky: true, direction: "top" });
      },
    }).addTo(map);

    layersRef.current = geoLayer;

    markersRef.current = STUDY_LANDSCAPES.map((place) => {
      const marker = L.circleMarker([place.lat, place.lon], {
        radius: 8,
        color: "#ffffff",
        weight: 2,
        fillColor: "#c47b2c",
        fillOpacity: 1,
      });

      marker.bindTooltip(
        `<strong>${place.label}</strong><br/>${
          place.prefectureId === "forecariah" ? "Préfecture de Forécariah" : "Préfecture de Kindia"
        }<br/>${STUDY_TERRITORY_TREE.label}`,
        { direction: "top", offset: [0, -6] },
      );

      if (interactive) {
        marker.on("click", () => onSelectRef.current(place.id));
      }

      marker.addTo(map);
      return { id: place.id, marker };
    });

    // Default frame: zoom tightly on the two study prefectures + locality markers.
    const studyFeatures = collection.features.filter(
      (f) => f.properties.id === "kindia" || f.properties.id === "forecariah",
    );
    const focusLayer = L.featureGroup([
      ...studyFeatures.map((f) => L.geoJSON(f)),
      ...markersRef.current.map((entry) => entry.marker),
    ]);
    if (studyFeatures.length > 0) {
      map.fitBounds(focusLayer.getBounds().pad(0.18), { animate: false, maxZoom: 10 });
    } else {
      map.setView(STUDY_CENTER, 9);
    }

    mapRef.current = map;

    const resize = () => map.invalidateSize();
    const resizeTimer = window.setTimeout(resize, 80);
    window.addEventListener("resize", resize);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => resize()) : null;
    if (ro && el) ro.observe(el);

    return () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", resize);
      ro?.disconnect();
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
      markersRef.current = [];
    };
    // Map instance is created once; selection styling is synced below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only map bootstrap
  }, []);

  useEffect(() => {
    const geoLayer = layersRef.current;
    if (!geoLayer) return;

    geoLayer.eachLayer((layer) => {
      const feature = (
        layer as L.Layer & { feature?: GeoJSON.Feature<GeoJSON.Geometry, AdminProperties> }
      ).feature;
      if (!feature?.properties || !("setStyle" in layer)) return;
      const { level, id: prefId } = feature.properties;

      if (level === "country") {
        (layer as L.Path).setStyle(countryStyle());
        return;
      }
      if (level === "region") {
        (layer as L.Path).setStyle(regionStyle());
        return;
      }

      const emphasized =
        highlightAll || selectedPrefectureId == null || selectedPrefectureId === prefId;
      (layer as L.Path).setStyle(prefectureStyle(prefId, emphasized));
    });

    for (const entry of markersRef.current) {
      const active = selectedId === entry.id || (highlightAll && selectedId == null);
      const muted = selectedId != null && selectedId !== entry.id;
      entry.marker.setStyle({
        radius: active ? 10 : 8,
        fillColor: active ? "#1f6f4a" : "#c47b2c",
        fillOpacity: muted ? 0.45 : 1,
        opacity: muted ? 0.55 : 1,
        weight: 2,
        color: "#fff",
      });
    }
  }, [selectedId, selectedPrefectureId, highlightAll]);

  return (
    <figure className="nbs-story-map" data-testid="guinea-landscape-map">
      <div className="nbs-story-map__frame">
        <div
          ref={containerRef}
          className="nbs-story-map__leaflet"
          role="region"
          aria-label={`Carte géographique de la Guinée — ${STUDY_TERRITORY_TREE.label}`}
        />
        <ul className="nbs-story-map__legend" aria-label="Légende de la carte">
          <li>
            <span className="nbs-story-map__swatch nbs-story-map__swatch--country" /> Guinée
          </li>
          <li>
            <span className="nbs-story-map__swatch nbs-story-map__swatch--region" /> Région de
            Kindia
          </li>
          <li>
            <span className="nbs-story-map__swatch nbs-story-map__swatch--kindia" /> Préf. Kindia
          </li>
          <li>
            <span className="nbs-story-map__swatch nbs-story-map__swatch--forecariah" /> Préf.
            Forécariah
          </li>
          <li>
            <span className="nbs-story-map__swatch nbs-story-map__swatch--locality" /> Localités
            étudiées
          </li>
        </ul>
        {overlay ? <div className="nbs-story-map__overlay">{overlay}</div> : null}
      </div>
      {showCaption ? (
        <figcaption className="nbs-story-map__caption">
          Carte géographique de la Guinée (OpenStreetMap). Contours : pays, région de Kindia, puis
          préfectures de Kindia et Forécariah — localités en points.
        </figcaption>
      ) : null}
    </figure>
  );
}
